import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/gallery", label: "Gallery" },
    { to: "/contact", label: "Contact" },
];

const Header = () => {
    const { settings } = useSettings();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    const phone = settings?.phone || "+919326725737";
    const whatsapp = (settings?.whatsapp || phone).replace(/[^\d]/g, "");
    const logoUrl = settings?.logo_url;

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-gradient-to-b from-black/70 to-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-20">
                <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group">
                    {logoUrl ? (
                        <img src={logoUrl} alt="Star Events" className="h-11 w-auto object-contain" />
                    ) : (
                        <div className="flex items-center gap-2">
                            <span className="text-3xl gold-text-gradient font-serif font-bold leading-none">★</span>
                            <div className="leading-tight">
                                <div className="font-serif text-lg md:text-xl font-bold text-white tracking-wide">Star Events</div>
                                <div className="text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-gold/80">Sales · Service · Rentals</div>
                            </div>
                        </div>
                    )}
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `text-sm uppercase tracking-[0.18em] transition-colors duration-300 ${
                                    isActive ? "text-gold" : "text-white/85 hover:text-gold"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href={`tel:${phone}`}
                        data-testid="header-call-btn"
                        className="flex items-center gap-2 text-sm text-white hover:text-gold transition-colors"
                    >
                        <Phone size={16} className="text-gold" />
                        <span className="font-medium">{phone}</span>
                    </a>
                    <a
                        href={`https://wa.me/${whatsapp}`}
                        target="_blank" rel="noreferrer"
                        data-testid="header-whatsapp-btn"
                        className="btn-gold text-sm"
                    >
                        WhatsApp
                    </a>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen(!open)}
                    className="lg:hidden text-white p-2"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div data-testid="mobile-menu" className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
                    <nav className="flex flex-col py-4 px-6 gap-4">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-base font-medium tracking-wide ${isActive ? "text-gold" : "text-white/90"}`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <div className="flex flex-col gap-3 pt-3 border-t border-white/10">
                            <a href={`tel:${phone}`} className="flex items-center gap-2 text-white">
                                <Phone size={16} className="text-gold" /> {phone}
                            </a>
                            <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="btn-gold text-sm text-center">
                                <MessageCircle size={16} className="inline mr-2" />WhatsApp Us
                            </a>
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
